import { TaskType } from '@/lib/sdk/chatbot/types';
import type { MasterDataFieldDefinition } from '@/lib/sdk/master-data-field-definitions/types';
import { ALL_TYPE_OPTIONS } from './taskGridConstants';
import type { GridRow } from './useTaskGridReducer';

/** A task as the config panel knows it, plus what the picker needs to list its values. */
export interface PanelTask {
  id: string;
  name: string;
  index: number;
  type: string;
  /** The master data dataset the task matches against, if any. */
  dataset?: string;
  /** Fields a verified photo, document or media upload extracts. */
  extractionFields?: string[];
}

/** One task the picker lists. */
export interface ValueRefTask {
  row: number;
  name: string;
  typeLabel: string;
  /** Whether the answer has a code and a label: a list pick, or a master data match. */
  hasCodeLabel: boolean;
  fields: { name: string; label: string }[];
  /** Set when this task can't be read here, e.g. it is answered later. */
  unavailableReason?: string;
}

const TYPE_LABELS = new Map<string, string>(ALL_TYPE_OPTIONS.map((option) => [option.value, option.label]));
const CODE_LABEL_TYPES = new Set<string>([TaskType.DYNAMIC_CHOICE, TaskType.TEXT, TaskType.LOCATION_CHECK]);

/**
 * Builds the picker's task list. Tasks after `lastReadableRow` are listed but marked as
 * answered later; leave it undefined when every task can be read (a workflow link).
 */
export function buildValueRefTasks(
  tasks: PanelTask[],
  fieldDefsByDataset: Record<string, MasterDataFieldDefinition[]>,
  lastReadableRow?: number,
): ValueRefTask[] {
  return tasks.map((task) => {
    const datasetFields = task.dataset
      ? (fieldDefsByDataset[task.dataset] ?? []).map((def) => ({ name: def.field_name, label: def.label || def.field_name }))
      : [];
    const extractionFields = (task.extractionFields ?? []).map((name) => ({ name, label: name }));
    const hasDataset = !!task.dataset;
    return {
      row: task.index,
      name: task.name,
      typeLabel: TYPE_LABELS.get(task.type) ?? task.type,
      // dynamic_choice answers are "ID|Title"; a text or location check matched against
      // master data gets its code and label from the match.
      hasCodeLabel: task.type === TaskType.DYNAMIC_CHOICE || (hasDataset && CODE_LABEL_TYPES.has(task.type)),
      fields: [...datasetFields, ...extractionFields],
      unavailableReason:
        lastReadableRow !== undefined && task.index > lastReadableRow
          ? 'Answered after this task, so it would be empty.'
          : undefined,
    };
  });
}

/** The panel's view of a grid row at the given row number. */
export function toPanelTask(row: GridRow, index: number): PanelTask {
  const extraction = row.extractionConfig;
  return {
    id: row.id,
    name: row.name,
    index,
    type: row.type,
    dataset: row.dataSourceConfig?.dataset || undefined,
    extractionFields: extraction?.enabled ? [...(extraction.required_fields ?? []), ...(extraction.optional_fields ?? [])] : undefined,
  };
}
